import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import {
  adminFetchBoard,
  adminFetchBoards,
  adminFetchAdmins,
  adminFetchBtwLabels,
  adminLookupIssueKey,
  adminUpdateBtwWorkflowStatus,
  adminUpdateApplicationStatus,
  adminUpdateApplicationWorkflowStatus,
  adminUpdateBtwAssignee,
  adminUpdateApplicationAssignee,
} from "../../core/axios/endpoints";
import { useUser } from "../../core/context/user";
import BoardCard, { DraggableBoardCard } from "../../components/board-card";
import BoardSettingsModal from "../../components/board-settings-modal";
import BtwAssigneeAvatar from "../../components/btw-assignee-avatar";
import BtwAssigneeMenu from "../../components/btw-assignee-menu";
import BtwLabelPill from "../../components/btw-label-pill";
import BtwStatusPill from "../../components/btw-status-pill";
import StatusLozenge, { categoryOf } from "../../components/status-lozenge";
import { getStatusColor, daysSince } from "../../core/btw-status-colors";
import {
  PARTNER_LINE_BADGE,
  SUBSIDIE_STATUS_LABEL,
  TYPE_FILTER_OPTIONS,
  formatEuro,
  formatShortDate,
  subsidieTransitionAllowed,
  typeBadge,
} from "../../core/board-items";
import { saveBoardLastUrl } from "../../core/board-last-url";

const useDebounced = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

const UNMAPPED = "__unmapped__";

// Het virtuele bord "alle" toont alle klanten; de sidebar opent het per type
// (BTW Batterij / EIA / EIA Zon / ISDE) met type-specifieke rijen.
const TYPE_TITLE = { btw: "BTW Batterij", eia: "EIA", eiaZon: "EIA Zon", isde: "ISDE", subsidie: "Subsidie" };
const CLASSIC_LIST = {
  btw: "/panel/admin/btw-batterij-overzicht",
  eia: "/panel/admin/eia-overzicht",
  eiaZon: "/panel/admin/eia-zon-overzicht",
  isde: "/panel/admin/isde-overzicht",
};
const listModeFor = (type) => (type === "btw" ? "btw" : ["eia", "eiaZon", "isde", "subsidie"].includes(type) ? "subsidie" : "mixed");
// Type-filter -> statusmodule. Elk type heeft eigen kolommen (statussen).
const moduleForType = (type) => (type === "btw" ? "btwBatterij" : ["eia", "eiaZon", "isde"].includes(type) ? type : null);
const MODULE_ORDER = ["btwBatterij", "eia", "eiaZon", "isde"];
const MODULE_LABEL = { btwBatterij: "BTW Batterij", eia: "EIA", eiaZon: "EIA Zon", isde: "ISDE" };

const Column = ({ status, items, isOver, droppable = true, showClient = false }) => {
  const { setNodeRef } = useDroppable({
    id: `col:${status._id}`,
    data: { statusId: status._id, status },
    disabled: !droppable,
  });
  const c = getStatusColor(status.color);
  const cat = categoryOf(status);

  return (
    <div
      ref={setNodeRef}
      className={`flex h-full w-full flex-col rounded bg-sunken transition md:w-[270px] md:flex-shrink-0 ${
        isOver ? "ring-2 ring-brand" : ""
      }`}
    >
      <div className="flex flex-shrink-0 items-center justify-between px-3 pb-1 pt-3">
        <div className="flex min-w-0 items-center gap-2">
          <span className={`h-2 w-2 flex-shrink-0 rounded-full ${c.dot}`} />
          <span className="truncate text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
            {status.name}
          </span>
          <span className="text-[11px] font-medium text-ink-subtlest tabular-nums">{items.length}</span>
        </div>
        {cat === "done" ? (
          <svg className="h-3.5 w-3.5 text-brand" viewBox="0 0 20 20" fill="currentColor" aria-label="Klaar">
            <path fillRule="evenodd" d="M16.7 5.3a1 1 0 010 1.4l-8 8a1 1 0 01-1.4 0l-4-4a1 1 0 011.4-1.4L8 12.6l7.3-7.3a1 1 0 011.4 0z" clipRule="evenodd" />
          </svg>
        ) : null}
      </div>
      <div className="space-y-1.5 overflow-y-auto px-1.5 pb-1.5" style={{ flex: 1, minHeight: 0 }}>
        {items.length === 0 ? (
          <div className="flex min-h-[80px] items-center justify-center rounded border border-dashed border-line text-xs text-ink-disabled">
            Leeg
          </div>
        ) : (
          items.map((it) => <DraggableBoardCard key={`${it.model}:${it.id}`} item={it} showClient={showClient} />)
        )}
      </div>
    </div>
  );
};

// Bevestiging voor het verslepen van een subsidie-aanvraag: dat wijzigt de
// echte status (Goedgekeurd maakt een factuur of partner-factuurregel aan),
// dus zelfde keuzes als op de detailpagina: reden bij Afgekeurd, mail-vinkje.
const SubsidieMoveModal = ({ move, onCancel, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !busy && onCancel();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [busy, onCancel]);
  if (!move) return null;
  const isReject = move.toStatus === "Afgekeurd";
  const label = SUBSIDIE_STATUS_LABEL[move.toStatus] ?? move.toStatus;
  const submit = async () => {
    if (isReject && !reason.trim()) {
      setError("Geef een reden op voor de afkeuring.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await onConfirm({ afgekeurdReason: isReject ? reason.trim() : undefined, notifyCustomer: notify });
    } catch (err) {
      setError(err?.response?.data?.error ?? err?.response?.data?.message ?? "Status wijzigen mislukt.");
      setBusy(false);
    }
  };
  return createPortal(
    <div role="dialog" aria-modal="true" style={{ position: "fixed", inset: 0, zIndex: 9999 }} className="flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-[#091E428A]" onClick={() => !busy && onCancel()} />
      <div className="relative w-full max-w-md rounded-md bg-white p-6 shadow-overlay">
        <h3 className="text-xl font-medium text-ink">
          {move.item.issueKey ?? move.item.title} naar {label}
        </h3>
        <p className="mt-2 text-sm text-ink-subtle">
          Dit wijzigt de status van de subsidie-aanvraag, net als op de detailpagina.
          {move.toStatus === "Goedgekeurd"
            ? " Bij Goedgekeurd wordt de factuur (of de partner-factuurregel) aangemaakt."
            : ""}
        </p>
        {isReject ? (
          <label className="mt-4 block">
            <span className="text-xs font-semibold text-ink-subtlest">Reden van afkeuring</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              autoFocus
              className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none"
            />
          </label>
        ) : null}
        <label className="mt-4 flex items-center gap-2 text-sm text-ink">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} className="h-4 w-4 rounded border-line text-brand focus:ring-0" />
          Klant een e-mail sturen over deze wijziging
        </label>
        {error ? <div className="mt-3 rounded bg-[#FFECEB] px-3 py-2 text-sm text-[#AE2A19]">{error}</div> : null}
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onCancel} disabled={busy} className="inline-flex h-8 items-center rounded bg-[#091E420F] px-3 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:cursor-not-allowed disabled:opacity-50">
            Annuleren
          </button>
          <button type="button" onClick={submit} disabled={busy} className={`inline-flex h-8 items-center rounded px-3 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50 ${isReject ? "bg-[#C9372C] hover:bg-[#AE2A19]" : "bg-brand hover:bg-brand-hover"}`}>
            {busy ? "Bezig..." : `Zet op ${label}`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// Compacte bordkeuze in de breadcrumb (Jira: projectkeuze linksboven).
const BoardSwitcher = ({ boards, current, type, navigate }) => {
  const [open, setOpen] = useState(false);
  const cur = current === "alle" ? { name: "Alle klanten", key: "ALLE" } : boards.find((b) => b._id === current);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-ink-subtle hover:bg-[#091E420F]"
      >
        <span className="inline-flex h-4 w-4 items-center justify-center rounded-sm bg-brand text-[8px] font-bold text-white">{(cur?.key ?? "?").slice(0, 3)}</span>
        {cur?.name ?? "Bord"}
        <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" /></svg>
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute left-0 z-30 mt-1 w-64 rounded bg-white p-1 shadow-overlay">
            <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">Bord</div>
            <button type="button" onClick={() => { setOpen(false); navigate(`/panel/admin/borden/alle${type ? `?type=${type}` : ""}`); }} className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-[#091E420F] ${current === "alle" ? "bg-brand-subtle text-brand" : "text-ink"}`}>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-sm bg-ink-subtle text-[9px] font-bold text-white">ALL</span>
              Alle klanten
            </button>
            {boards.map((b) => (
              <button key={b._id} type="button" onClick={() => { setOpen(false); navigate(`/panel/admin/borden/${b._id}`); }} className={`flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-[#091E420F] ${current === b._id ? "bg-brand-subtle text-brand" : "text-ink"}`}>
                <span className={`inline-flex h-5 w-5 items-center justify-center rounded-sm text-[9px] font-bold text-white ${b.kind === "direct" ? "bg-ink-subtle" : "bg-brand"}`}>{(b.key ?? "?").slice(0, 3)}</span>
                <span className="truncate">{b.name}</span>
                <span className="ml-auto text-[11px] text-ink-subtlest">{b.key}</span>
              </button>
            ))}
            <div className="my-1 border-t border-line" />
            <Link to="/panel/admin/borden" onClick={() => setOpen(false)} className="block rounded px-3 py-2 text-sm text-brand hover:bg-[#091E420F]">Bordenoverzicht</Link>
          </div>
        </>
      ) : null}
    </div>
  );
};

const Toast = ({ toast, onClose }) => {
  // Timer alleen herstarten bij een nieuwe toast, niet bij elke render van
  // de pagina (typen, drag-over), vandaar de ref voor onClose.
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => onCloseRef.current?.(), 4000);
    return () => clearTimeout(t);
  }, [toast]);
  if (!toast) return null;
  return (
    <div className={`fixed bottom-4 right-4 z-[9998] max-w-sm rounded px-4 py-3 text-sm shadow-overlay ${toast.kind === "error" ? "bg-[#FFECEB] text-[#AE2A19]" : "bg-white text-ink"}`}>
      {toast.text}
    </div>
  );
};

const readFilters = (sp) => ({
  view: sp.get("view") === "list" ? "list" : "board",
  search: sp.get("search") ?? "",
  type: sp.get("type") ?? "",
  mine: sp.get("mine") === "1",
  unassigned: sp.get("unassigned") === "1",
  assignedToAdminId: sp.get("assignedToAdminId") ?? "",
  labelId: sp.get("labelId") ?? "",
  deadline: sp.get("deadline") ?? "",
  stuck: sp.get("stuck") === "1",
  archived: sp.get("archived") ?? "",
  done: sp.get("done") !== "0",
});

const BoardPage = () => {
  const { boardId } = useParams();
  const navigate = useNavigate();
  const { userData } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => readFilters(searchParams));
  const setFilter = useCallback((k, v) => setFilters((f) => ({ ...f, [k]: v })), []);

  const [board, setBoard] = useState(null);
  const [boards, setBoards] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [items, setItems] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [activeId, setActiveId] = useState(null);
  const [overColId, setOverColId] = useState(null);
  const [pendingMove, setPendingMove] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const debouncedSearch = useDebounced(filters.search, 350);
  // Virtueel bord "alle" (per type vanuit de sidebar) vs. een klantbord.
  const isAll = boardId === "alle" || board?.kind === "all";
  const listMode = listModeFor(filters.type);

  // URL-state zodat filters de detailpagina en de terugknop overleven.
  useEffect(() => {
    const p = new URLSearchParams();
    if (filters.view === "list") p.set("view", "list");
    if (filters.search) p.set("search", filters.search);
    if (filters.type) p.set("type", filters.type);
    if (filters.mine) p.set("mine", "1");
    if (filters.unassigned) p.set("unassigned", "1");
    if (filters.assignedToAdminId) p.set("assignedToAdminId", filters.assignedToAdminId);
    if (filters.labelId) p.set("labelId", filters.labelId);
    if (filters.deadline) p.set("deadline", filters.deadline);
    if (filters.stuck) p.set("stuck", "1");
    if (filters.archived) p.set("archived", filters.archived);
    if (!filters.done) p.set("done", "0");
    setSearchParams(p, { replace: true });
    const qs = p.toString();
    saveBoardLastUrl(`/panel/admin/borden/${boardId}`, qs ? `?${qs}` : "");
  }, [filters, boardId, setSearchParams]);

  useEffect(() => {
    Promise.all([adminFetchAdmins(), adminFetchBtwLabels(), adminFetchBoards()])
      .then(([a, l, b]) => {
        setAdmins(a?.data ?? a ?? []);
        setLabels(l?.data ?? l ?? []);
        setBoards(b?.data ?? b ?? []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    adminFetchBoard(boardId, {
      search: debouncedSearch,
      type: filters.type,
      mine: filters.mine,
      unassigned: filters.unassigned,
      assignedToAdminId: filters.assignedToAdminId,
      labelId: filters.labelId,
      deadline: filters.deadline,
      stuck: filters.stuck,
      archived: filters.archived,
    })
      .then((res) => {
        if (cancelled) return;
        const d = res?.data ?? res;
        setBoard(d.board);
        setStatuses(d.statuses ?? []);
        setItems(d.items ?? []);
        setNotFound(false);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err?.response?.status === 404) setNotFound(true);
        else console.error("Bord laden mislukt", err);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [
    boardId,
    debouncedSearch,
    filters.type,
    filters.mine,
    filters.unassigned,
    filters.assignedToAdminId,
    filters.labelId,
    filters.deadline,
    filters.stuck,
    filters.archived,
    reloadKey,
  ]);

  // Bordkolommen: de statussen van één type. Zonder type-filter (klantbord,
  // "Alles") kiezen we het eerste type dat op het bord voorkomt; de chips
  // schakelen tussen de types. De lijstweergave toont wel alles.
  const effectiveModule = useMemo(() => {
    const fromType = moduleForType(filters.type);
    if (fromType) return fromType;
    const present = new Set(items.map((it) => it.module));
    return MODULE_ORDER.find((m) => present.has(m)) ?? "btwBatterij";
  }, [filters.type, items]);
  const visibleStatuses = useMemo(
    () =>
      statuses.filter(
        (s) => !s.archived && (filters.done || categoryOf(s) !== "done") && (s.module ?? "btwBatterij") === effectiveModule,
      ),
    [statuses, filters.done, effectiveModule],
  );
  const boardItems = useMemo(
    () => (moduleForType(filters.type) ? items : items.filter((it) => it.module === effectiveModule)),
    [items, filters.type, effectiveModule],
  );

  const grouped = useMemo(() => {
    const map = new Map();
    visibleStatuses.forEach((s) => map.set(String(s._id), []));
    const unmapped = [];
    boardItems.forEach((it) => {
      const sid = it.workflowStatusId ? String(it.workflowStatusId) : null;
      if (sid && map.has(sid)) map.get(sid).push(it);
      else if (!sid) unmapped.push(it);
      // item in een verborgen (klaar/gearchiveerde) kolom: niet tonen
    });
    map.set(UNMAPPED, unmapped);
    return map;
  }, [boardItems, visibleStatuses]);

  const unmappedItems = grouped.get(UNMAPPED) ?? [];

  // Mobile: één kolom tegelijk.
  const [activeStatusId, setActiveStatusId] = useState(null);
  useEffect(() => {
    if (visibleStatuses.length === 0) return;
    if (!activeStatusId || !visibleStatuses.find((s) => String(s._id) === String(activeStatusId))) {
      setActiveStatusId(String(visibleStatuses[0]._id));
    }
  }, [visibleStatuses, activeStatusId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const activeItem = useMemo(
    () => items.find((it) => `${it.model}:${it.id}` === activeId) ?? null,
    [activeId, items],
  );

  const patchItem = (model, id, patch) =>
    setItems((prev) => prev.map((it) => (it.model === model && it.id === id ? { ...it, ...patch } : it)));

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColId(null);
    if (!over) return;
    const targetStatus = over.data?.current?.status;
    if (!targetStatus) return;
    const item = active.data?.current?.item;
    if (!item) return;
    if (String(item.workflowStatusId ?? "") === String(targetStatus._id)) return;

    const targetModule = targetStatus.module ?? "btwBatterij";
    if (targetModule !== item.module) {
      setToast({ kind: "error", text: `De kolom "${targetStatus.name}" hoort bij ${MODULE_LABEL[targetModule] ?? targetModule}, niet bij ${MODULE_LABEL[item.module] ?? item.module}.` });
      return;
    }

    if (item.model === "btw") {
      const prev = item;
      patchItem("btw", item.id, { workflowStatusId: String(targetStatus._id), lastActivityAt: new Date().toISOString() });
      try {
        await adminUpdateBtwWorkflowStatus(item.id, targetStatus._id);
      } catch (err) {
        patchItem("btw", item.id, prev);
        setToast({ kind: "error", text: err?.response?.data?.error ?? "Status wijzigen mislukt." });
      }
      return;
    }

    // Subsidie: vrije kolom = alleen de bordkolom wijzigen. Een kolom die aan
    // een RVO-status gekoppeld is, loopt via de echte statuswijziging (met
    // bevestiging), en alleen de overgangen die de server toestaat.
    if (!targetStatus.subsidieStatus || targetStatus.subsidieStatus === item.businessStatus) {
      const prev = item;
      patchItem("subsidie", item.id, { workflowStatusId: String(targetStatus._id), lastActivityAt: new Date().toISOString() });
      try {
        await adminUpdateApplicationWorkflowStatus(item.id, targetStatus._id);
      } catch (err) {
        patchItem("subsidie", item.id, prev);
        setToast({ kind: "error", text: err?.response?.data?.error ?? "Kolom wijzigen mislukt." });
      }
      return;
    }
    if (!subsidieTransitionAllowed(item.businessStatus, targetStatus.subsidieStatus)) {
      setToast({
        kind: "error",
        text: `Een subsidie-aanvraag kan niet van ${SUBSIDIE_STATUS_LABEL[item.businessStatus] ?? item.businessStatus} naar ${SUBSIDIE_STATUS_LABEL[targetStatus.subsidieStatus] ?? targetStatus.subsidieStatus}.`,
      });
      return;
    }
    setPendingMove({ item, toStatus: targetStatus.subsidieStatus, targetStatus });
  };

  const confirmSubsidieMove = async ({ afgekeurdReason, notifyCustomer }) => {
    const { item, toStatus, targetStatus } = pendingMove;
    const payload = { status: toStatus, notifyCustomer };
    if (afgekeurdReason) payload.afgekeurdReason = afgekeurdReason;
    const res = await adminUpdateApplicationStatus(item.id, payload);
    patchItem("subsidie", item.id, {
      businessStatus: toStatus,
      workflowStatusId: String(targetStatus._id),
      lastActivityAt: new Date().toISOString(),
    });
    setPendingMove(null);
    const d = res?.data ?? res;
    if (d?.emailError) setToast({ kind: "error", text: `Status gewijzigd, maar de mail is niet verstuurd: ${d.emailError}` });
    else setToast({ kind: "ok", text: `${item.issueKey ?? item.title} staat nu op ${SUBSIDIE_STATUS_LABEL[toStatus] ?? toStatus}.` });
  };

  const changeAssignee = async (item, picked, comment) => {
    const id = picked?._id ?? null;
    try {
      if (item.model === "btw") await adminUpdateBtwAssignee(item.id, id, comment);
      else await adminUpdateApplicationAssignee(item.id, id);
      patchItem(item.model, item.id, { assignedToAdminId: picked ?? null });
    } catch (err) {
      setToast({ kind: "error", text: err?.response?.data?.error ?? "Toewijzen mislukt." });
      throw err;
    }
  };

  // Exacte key in het zoekveld + Enter: direct naar de aanvraag.
  const onSearchKeyDown = async (e) => {
    if (e.key !== "Enter") return;
    const v = filters.search.trim().toUpperCase();
    if (!/^[A-Z][A-Z0-9]{1,9}-\d+$/.test(v)) return;
    try {
      const res = await adminLookupIssueKey(v);
      const hit = res?.data ?? res;
      const local = items.find((it) => it.id === hit.id);
      if (local) navigate(local.detailPath);
      else if (hit.detailPath) navigate(hit.detailPath);
      else navigate(`/panel/admin/borden/${hit.boardId ?? boardId}?search=${encodeURIComponent(v)}`);
    } catch {
      setToast({ kind: "error", text: `Geen aanvraag gevonden met key ${v}.` });
    }
  };

  const resetFilters = () =>
    setFilters((f) => ({
      ...f,
      search: "",
      type: "",
      mine: false,
      unassigned: false,
      assignedToAdminId: "",
      labelId: "",
      deadline: "",
      stuck: false,
      archived: "",
    }));

  const activeFilterCount =
    (filters.search ? 1 : 0) +
    (filters.type && !isAll ? 1 : 0) +
    (filters.mine ? 1 : 0) +
    (filters.unassigned ? 1 : 0) +
    (filters.assignedToAdminId ? 1 : 0) +
    (filters.labelId ? 1 : 0) +
    (filters.deadline ? 1 : 0) +
    (filters.stuck ? 1 : 0) +
    (filters.archived ? 1 : 0);

  const clientName = board?.clientUserId?.bedrijfsnaam || board?.clientUserId?.naam;

  if (notFound) {
    return (
      <div className="page-wrapper px-8 pt-20 pb-6">
        <div className="rounded bg-white p-10 text-center shadow-card">
          <p className="text-sm text-ink">Dit bord bestaat niet.</p>
          <Link to="/panel/admin/borden" className="mt-3 inline-block text-sm text-brand hover:underline">
            Naar alle borden
          </Link>
        </div>
      </div>
    );
  }

  const avatarRow = (
    <div className="flex items-center -space-x-1.5">
      {admins.slice(0, 8).map((a) => {
        const active = filters.assignedToAdminId === a._id || (filters.mine && a._id === userData?._id);
        return (
          <button
            key={a._id}
            type="button"
            title={a.naam}
            onClick={() =>
              setFilters((f) => ({
                ...f,
                mine: false,
                unassigned: false,
                assignedToAdminId: f.assignedToAdminId === a._id ? "" : a._id,
              }))
            }
            className={`rounded-full transition hover:z-10 hover:scale-110 ${active ? "z-10 ring-2 ring-brand ring-offset-1" : "ring-2 ring-white"}`}
          >
            <BtwAssigneeAvatar user={a} size="sm" />
          </button>
        );
      })}
      <button
        type="button"
        title="Niet toegewezen"
        onClick={() => setFilters((f) => ({ ...f, mine: false, assignedToAdminId: "", unassigned: !f.unassigned }))}
        className={`rounded-full transition hover:z-10 hover:scale-110 ${filters.unassigned ? "z-10 ring-2 ring-brand ring-offset-1" : "ring-2 ring-white"}`}
      >
        <BtwAssigneeAvatar user={null} size="sm" />
      </button>
    </div>
  );

  return (
    <div className="page-wrapper px-8 pt-20 pb-4" style={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <nav className="mb-1 flex items-center gap-1 text-xs text-ink-subtlest" aria-label="Breadcrumb">
        <BoardSwitcher boards={boards} current={isAll ? "alle" : board?._id} type={filters.type} navigate={navigate} />
        <span className="mx-1 h-3 w-px bg-line" />
        {isAll ? (
          <>
            <Link to="/panel/admin/borden/alle" className="hover:underline">Aanvragen</Link>
            <span>/</span>
            <span className="text-ink-subtle">{TYPE_TITLE[filters.type] ?? "Alle"}</span>
          </>
        ) : (
          <>
            <Link to="/panel/admin/borden" className="hover:underline">Borden</Link>
            <span>/</span>
            <span className="text-ink-subtle">{board?.name ?? "…"}</span>
          </>
        )}
      </nav>
      {/* Kop: bordkeuze, naam, acties */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <select
            value={isAll ? "alle" : board?._id ?? ""}
            onChange={(e) => e.target.value && navigate(`/panel/admin/borden/${e.target.value}${e.target.value === "alle" && filters.type ? `?type=${filters.type}` : ""}`)}
            className="hidden"
            aria-label="Bord kiezen"
          >
            {!board ? <option value="">Bord laden…</option> : null}
            <option value="alle">Alle aanvragen (alle klanten)</option>
            {boards.map((b) => (
              <option key={b._id} value={b._id}>
                {b.name} ({b.key})
              </option>
            ))}
            {board && !isAll && !boards.find((b) => b._id === board._id) ? (
              <option value={board._id}>{board.name} ({board.key})</option>
            ) : null}
          </select>
          <div className="min-w-0">
            <h2 className="flex items-center gap-2 truncate text-2xl font-medium text-ink">
              {isAll ? (TYPE_TITLE[filters.type] ?? "Alle aanvragen") : (board?.name ?? "Bord")}
              {board?.key && !isAll ? (
                <span className="text-xs font-medium text-ink-subtlest">
                  {board.key}
                </span>
              ) : null}
              {board?.archived ? (
                <span className="inline-flex items-center rounded bg-[#091E420F] px-1.5 py-px text-[11px] font-bold uppercase tracking-wide text-ink-subtle">
                  Gearchiveerd
                </span>
              ) : null}
            </h2>
            <p className="truncate text-xs text-ink-subtlest">
              {isAll
                ? "Alle klanten"
                : board?.kind === "direct"
                ? "Aanvragen zonder partner (klant betaalt zelf of via het portaal)"
                : clientName
                ? `Partner: ${clientName}`
                : ""}
              {" · "}
              {items.length} {items.length === 1 ? "aanvraag" : "aanvragen"}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded bg-[#091E420F] p-0.5">
            {["board", "list"].map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setFilter("view", v)}
                className={`rounded px-3 py-1 text-sm font-medium transition ${filters.view === v ? "bg-white text-brand shadow-card" : "text-ink-subtle hover:bg-[#091E420F]"}`}
              >
                {v === "board" ? "Bord" : "Lijst"}
              </button>
            ))}
          </div>
          {CLASSIC_LIST[filters.type] ? (
            <Link to={CLASSIC_LIST[filters.type]} className="rounded bg-[#091E420F] px-3 py-1.5 text-sm font-medium text-ink hover:bg-[#091E4224]" title="Beheerlijst met bulkacties, export en archief">
              Beheerlijst
            </Link>
          ) : null}
          <Link to="/panel/admin/btw-batterij-instellingen" className="rounded bg-[#091E420F] px-3 py-1.5 text-sm font-medium text-ink hover:bg-[#091E4224]">
            Statussen
          </Link>
          {!isAll ? (
            <button type="button" onClick={() => setSettingsOpen(true)} disabled={!board} className="rounded bg-[#091E420F] px-3 py-1.5 text-sm font-medium text-ink hover:bg-[#091E4224] disabled:opacity-50">
              Bordinstellingen
            </button>
          ) : null}
        </div>
      </div>

      {/* Filterrij: zoeken, type, avatars, meer */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative">
          <svg className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtlest" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.45 4.4l3.57 3.58a1 1 0 01-1.42 1.42l-3.57-3.58A7 7 0 012 9z" clipRule="evenodd" />
          </svg>
          <input
            type="search"
            value={filters.search}
            onChange={(e) => setFilter("search", e.target.value)}
            onKeyDown={onSearchKeyDown}
            placeholder="Zoek op naam, key (SCN-12), kenmerk…"
            className="w-64 rounded border-2 border-line bg-white py-1.5 pl-8 pr-3 text-sm focus:border-brand focus:outline-none"
          />
        </div>

        <div className="inline-flex flex-wrap gap-1">
          {TYPE_FILTER_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setFilter("type", o.value)}
              className={`rounded px-2.5 py-1 text-xs font-medium transition ${
                filters.type === o.value ? "bg-brand-subtle text-brand" : "text-ink-subtle hover:bg-[#091E420F]"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>

        <div className="mx-1 hidden h-6 w-px bg-line sm:block" />
        {avatarRow}
        <button
          type="button"
          onClick={() => setFilters((f) => ({ ...f, assignedToAdminId: "", unassigned: false, mine: !f.mine }))}
          className={`rounded px-2.5 py-1 text-xs font-medium transition ${filters.mine ? "bg-brand-subtle text-brand" : "text-ink-subtle hover:bg-[#091E420F]"}`}
        >
          Mijn aanvragen
        </button>

        <div className="relative">
          <button
            type="button"
            onClick={() => setMoreOpen((o) => !o)}
            className={`rounded px-2.5 py-1 text-xs font-medium transition ${filters.labelId || filters.deadline || filters.stuck || filters.archived ? "bg-brand-subtle text-brand" : "text-ink-subtle hover:bg-[#091E420F]"}`}
          >
            Meer filters
          </button>
          {moreOpen ? (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setMoreOpen(false)} />
              <div className="absolute left-0 z-30 mt-2 w-72 space-y-3 rounded bg-white p-3 shadow-overlay">
                <label className="block text-xs">
                  <span className="text-xs font-semibold text-ink-subtlest">Label (BTW)</span>
                  <select value={filters.labelId} onChange={(e) => setFilter("labelId", e.target.value)} className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none">
                    <option value="">Alle</option>
                    {labels.map((l) => (
                      <option key={l._id} value={l._id}>{l.name}</option>
                    ))}
                  </select>
                </label>
                <label className="block text-xs">
                  <span className="text-xs font-semibold text-ink-subtlest">Deadline (BTW)</span>
                  <select value={filters.deadline} onChange={(e) => setFilter("deadline", e.target.value)} className="mt-1 w-full rounded border-2 border-line bg-white px-2 py-1.5 text-sm text-ink focus:border-brand focus:outline-none">
                    <option value="">Alle</option>
                    <option value="week">Binnen 7 dagen</option>
                    <option value="overdue">Verlopen</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={filters.stuck} onChange={(e) => setFilter("stuck", e.target.checked)} className="h-4 w-4 rounded border-line text-brand focus:ring-0" />
                  Vastgelopen (7+ dagen stil)
                </label>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={filters.archived === "true"} onChange={(e) => setFilter("archived", e.target.checked ? "true" : "")} className="h-4 w-4 rounded border-line text-brand focus:ring-0" />
                  Alleen archief (BTW)
                </label>
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input type="checkbox" checked={filters.done} onChange={(e) => setFilter("done", e.target.checked)} className="h-4 w-4 rounded border-line text-brand focus:ring-0" />
                  Toon Klaar-kolommen
                </label>
                <p className="text-[11px] text-ink-subtlest">Label, deadline en archief bestaan alleen voor BTW-aanvragen; met zo&rsquo;n filter aan vallen subsidie-aanvragen weg.</p>
              </div>
            </>
          ) : null}
        </div>

        {activeFilterCount > 0 ? (
          <button type="button" onClick={resetFilters} className="text-xs text-brand hover:underline">
            Wis filters ({activeFilterCount})
          </button>
        ) : null}
      </div>

      {loading && items.length === 0 && !board ? (
        <div className="rounded bg-white p-10 text-center text-sm text-ink-subtlest shadow-card">Bord laden…</div>
      ) : filters.view === "list" ? (
        <ListView
          items={items}
          statuses={statuses.filter((s) => !s.archived && (filters.done || categoryOf(s) !== "done"))}
          admins={admins}
          currentUserId={userData?._id}
          onAssign={changeAssignee}
          mode={listMode}
          showClient={isAll}
        />
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={(e) => setActiveId(e.active.id)}
          onDragOver={(e) => setOverColId(e.over?.data?.current?.statusId ?? null)}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setActiveId(null);
            setOverColId(null);
          }}
        >
          {/* Mobile: statustabs */}
          <div className="mb-3 -mx-1 flex gap-2 overflow-x-auto px-1 pb-1 md:hidden">
            {visibleStatuses.map((s) => {
              const count = (grouped.get(String(s._id)) ?? []).length;
              const isActive = String(s._id) === String(activeStatusId);
              const c = getStatusColor(s.color);
              return (
                <button key={s._id} type="button" onClick={() => setActiveStatusId(String(s._id))} className={`flex-none inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-xs font-medium transition ${isActive ? "bg-brand-subtle text-brand" : "text-ink-subtle hover:bg-[#091E420F]"}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${c.dot}`} />
                  {s.name}
                  <span className={`ml-0.5 rounded px-1.5 text-[10px] ${isActive ? "bg-white" : "bg-[#091E420F] text-ink-subtle"}`}>{count}</span>
                </button>
              );
            })}
          </div>

          <div className="hidden gap-2 overflow-x-auto pb-2 md:flex" style={{ flex: 1, minHeight: 0 }}>
            {visibleStatuses.map((s) => (
              <Column key={s._id} status={s} items={grouped.get(String(s._id)) ?? []} isOver={overColId === s._id} showClient={isAll} />
            ))}
            {unmappedItems.length > 0 ? (
              <Column
                status={{ _id: UNMAPPED, name: "Zonder kolom", color: "gray", category: "todo" }}
                items={unmappedItems}
                isOver={false}
                droppable={false}
              />
            ) : null}
          </div>

          <div className="block md:hidden" style={{ flex: 1, minHeight: 0 }}>
            {(() => {
              const s = visibleStatuses.find((x) => String(x._id) === String(activeStatusId));
              if (!s) return null;
              return <Column status={s} items={grouped.get(String(s._id)) ?? []} isOver={overColId === s._id} />;
            })()}
          </div>

          <DragOverlay dropAnimation={{ duration: 150 }}>
            {activeItem ? <BoardCard item={activeItem} dragging /> : null}
          </DragOverlay>
        </DndContext>
      )}

      {filters.view === "board" && !moduleForType(filters.type) && new Set(items.map((it) => it.module)).size > 1 ? (
        <p className="mt-2 text-[11px] text-ink-subtlest">
          Je ziet de kolommen van {MODULE_LABEL[effectiveModule]} ({boardItems.length} van {items.length} aanvragen). Kies een ander type via de chips, of open de lijst voor alles tegelijk.
        </p>
      ) : null}
      {unmappedItems.length > 0 && filters.view === "board" ? (
        <p className="mt-2 text-[11px] text-ink-subtlest">
          &ldquo;Zonder kolom&rdquo;: aanvragen zonder bordkolom (nog niet toegewezen door de server). Sleep ze naar een kolom of wacht op de automatische toewijzing.
        </p>
      ) : null}

      <SubsidieMoveModal move={pendingMove} onCancel={() => setPendingMove(null)} onConfirm={confirmSubsidieMove} />
      <BoardSettingsModal
        board={board}
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        onSaved={(b) => {
          setBoard((prev) => ({ ...prev, ...b }));
          setBoards((prev) => prev.map((x) => (x._id === b._id ? { ...x, ...b } : x)));
          window.dispatchEvent(new Event("boards:changed"));
          setReloadKey((k) => k + 1);
        }}
      />
      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

const LIST_COLUMNS = {
  btw: ["key", "aanvrager", "plaats", "status", "toegewezen", "labels", "deadline", "betaling", "activiteit"],
  subsidie: ["key", "bedrijf", "kvk", "kenmerk", "regeling", "status", "toegewezen", "datumOpdracht", "bedrag"],
  mixed: ["key", "type", "aanvraag", "status", "toegewezen", "labels", "deadline", "activiteit", "bedrag"],
};
const COL_LABEL = {
  key: "Key", type: "Type", aanvraag: "Aanvraag", aanvrager: "Aanvrager", bedrijf: "Bedrijf", plaats: "Plaats", kvk: "KvK", kenmerk: "Kenmerk",
  regeling: "Regeling", status: "Status", toegewezen: "Toegewezen", labels: "Labels", deadline: "Deadline", betaling: "Betaling",
  activiteit: "Activiteit", datumOpdracht: "Datum opdracht", bedrag: "Bedrag", klant: "Klant",
};
const RIGHT_COLS = new Set(["bedrag"]);
const SUBSIDIE_LOZENGE = {
  "In-behandeling": "bg-brand-subtle text-brand",
  "Ingediend-bij-rvo": "bg-[#F3F0FF] text-[#5E4DB2]",
  Goedgekeurd: "bg-[#DCFFF1] text-[#1F845A]",
  Afgekeurd: "bg-[#FFECEB] text-[#AE2A19]",
};

const ListView = ({ items, statuses, admins, currentUserId, onAssign, mode = "mixed", showClient = false }) => {
  const cols = useMemo(() => {
    const base = LIST_COLUMNS[mode] ?? LIST_COLUMNS.mixed;
    return showClient ? [...base.slice(0, 2), "klant", ...base.slice(2)] : base;
  }, [mode, showClient]);
  const statusById = useMemo(() => new Map(statuses.map((s) => [String(s._id), s])), [statuses]);
  const groups = useMemo(() => {
    const order = ["todo", "in_progress", "done"];
    const byCat = { todo: [], in_progress: [], done: [], none: [] };
    items.forEach((it) => {
      const s = it.workflowStatusId ? statusById.get(String(it.workflowStatusId)) : null;
      if (!s) {
        if (!it.workflowStatusId) byCat.none.push(it);
        return;
      }
      byCat[categoryOf(s)].push(it);
    });
    return [...order.map((c) => ({ cat: c, items: byCat[c] })), { cat: "none", items: byCat.none }].filter((g) => g.items.length > 0);
  }, [items, statusById]);

  const catLabel = { todo: "To do", in_progress: "Bezig", done: "Klaar", none: "Zonder kolom" };

  if (items.length === 0) {
    return <div className="rounded bg-white p-10 text-center text-sm text-ink-subtlest shadow-card">Geen aanvragen gevonden met deze filters.</div>;
  }

  return (
    <div className="rounded bg-white shadow-card">
      <table className="w-full text-sm">
        <thead className="bg-sunken text-left text-[11px] font-semibold uppercase tracking-wide text-ink-subtlest">
          <tr>
            {cols.map((c) => (
              <th key={c} className={`whitespace-nowrap px-3 py-2 ${RIGHT_COLS.has(c) ? "text-right" : ""}`}>{COL_LABEL[c]}</th>
            ))}
          </tr>
        </thead>
        {groups.map((g) => (
          <tbody key={g.cat} className="divide-y divide-line border-t border-line">
            <tr className="bg-sunken">
              <td colSpan={cols.length} className="px-3 py-1.5">
                <span className="inline-flex items-center gap-2">
                  <StatusLozenge category={g.cat === "none" ? "todo" : g.cat} text={catLabel[g.cat]} size="sm" />
                  <span className="text-[11px] text-ink-subtlest">{g.items.length}</span>
                </span>
              </td>
            </tr>
            {g.items.map((it) => {
              const s = it.workflowStatusId ? statusById.get(String(it.workflowStatusId)) : null;
              const t = typeBadge(it.module);
              const days = daysSince(it.lastActivityAt ?? it.createdAt);
              const ex = it.extra ?? {};
              const pl = it.partnerInvoiceStatus ? PARTNER_LINE_BADGE[it.partnerInvoiceStatus] : null;
              const cell = (c) => {
                switch (c) {
                  case "key":
                    return <Link to={it.detailPath} state={{ from: "board" }} className="whitespace-nowrap text-xs font-medium text-ink-subtlest hover:underline">{it.issueKey ?? "-"}</Link>;
                  case "type":
                    return <span className={`inline-flex items-center rounded px-1.5 py-px text-[10px] font-semibold ring-1 ring-inset ${t.cls}`}>{t.label}</span>;
                  case "klant":
                    return <span className="inline-flex max-w-[180px] items-center truncate whitespace-nowrap rounded bg-[#F3F0FF] px-1.5 py-px text-[11px] font-medium text-[#5E4DB2]">{it.partnerName ?? "Direct"}</span>;
                  case "aanvraag":
                  case "aanvrager":
                  case "bedrijf":
                    return (
                      <>
                        <Link to={it.detailPath} state={{ from: "board" }} className="block truncate font-medium text-ink hover:underline">{it.title}</Link>
                        <div className="truncate text-xs text-ink-subtlest">{c === "bedrijf" ? ex.contact ?? ex.email ?? "" : ex.email ?? it.subtitle}</div>
                      </>
                    );
                  case "plaats":
                    return <span className="text-ink-subtle">{ex.plaats ?? ""}</span>;
                  case "kvk":
                    return <span className="text-xs tabular-nums text-ink-subtle">{ex.kvk ?? ""}</span>;
                  case "kenmerk":
                    return <span className="text-xs text-ink-subtle">{ex.kenmerk ?? ""}</span>;
                  case "regeling":
                    return <span className={`inline-flex items-center rounded px-1.5 py-px text-[10px] font-semibold ring-1 ring-inset ${t.cls}`}>{t.label}</span>;
                  case "status":
                    return (
                      <span className="inline-flex flex-wrap items-center gap-1">
                        {s ? <BtwStatusPill status={s} size="sm" /> : <span className="text-xs text-ink-subtlest">-</span>}
                        {it.model === "subsidie" && it.businessStatus && (!s || s.subsidieStatus !== it.businessStatus) ? (
                          <span
                            title="RVO-status"
                            className={`inline-flex items-center whitespace-nowrap rounded px-1.5 py-px text-[10px] font-bold uppercase tracking-wide ${SUBSIDIE_LOZENGE[it.businessStatus] ?? "bg-[#091E420F] text-ink-subtle"}`}
                          >
                            {SUBSIDIE_STATUS_LABEL[it.businessStatus] ?? it.businessStatus}
                          </span>
                        ) : null}
                        {it.isStuck ? <span className="text-[10px] font-semibold text-[#AE2A19]">{days}d stil</span> : null}
                      </span>
                    );
                  case "toegewezen":
                    return (
                      <BtwAssigneeMenu
                        current={it.assignedToAdminId}
                        admins={admins}
                        currentUserId={currentUserId}
                        onSelect={(picked, comment) => onAssign(it, picked, comment)}
                      />
                    );
                  case "labels":
                    return <div className="flex flex-wrap gap-1">{(it.labelIds ?? []).map((l) => <BtwLabelPill key={l._id} label={l} size="sm" />)}</div>;
                  case "deadline":
                    return <span className="text-xs text-ink-subtle">{formatShortDate(it.deadlineAt)}</span>;
                  case "betaling":
                    return pl ? (
                      <span className={`inline-flex items-center rounded px-1.5 py-px text-[10px] font-medium ring-1 ring-inset ${pl.cls}`}>{pl.label}</span>
                    ) : it.amountIncl != null ? (
                      <span className="text-xs tabular-nums text-ink-subtle">{formatEuro(it.amountIncl)}</span>
                    ) : (
                      <span className="text-xs text-ink-subtlest">-</span>
                    );
                  case "activiteit":
                    return <span className="text-xs text-ink-subtlest">{days == null ? "" : days === 0 ? "vandaag" : `${days}d geleden`}</span>;
                  case "datumOpdracht":
                    return <span className="text-xs text-ink-subtle">{ex.datumOpdracht ?? ""}</span>;
                  case "bedrag":
                    return <span className="text-xs tabular-nums text-ink-subtle">{it.amountIncl != null ? formatEuro(it.amountIncl) : ""}</span>;
                  default:
                    return null;
                }
              };
              return (
                <tr key={`${it.model}:${it.id}`} className="transition-colors hover:bg-sunken">
                  {cols.map((c) => (
                    <td key={c} className={`px-3 py-2 align-middle ${RIGHT_COLS.has(c) ? "text-right" : ""} ${["aanvraag", "aanvrager", "bedrijf"].includes(c) ? "max-w-[280px]" : "whitespace-nowrap"}`}>
                      {cell(c)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        ))}
      </table>
    </div>
  );
};

export default BoardPage;
